// 배차(적재) 계산기 — 순수 함수만 둔다(React·네트워크 없음).
// 화면(DispatchCalcPage)·기본값 편집(DispatchDefaultsPanel)·시험(dispatchCalc.test.js)이 같이 쓴다.
//
// 계산 명세(요청서):
//  - 비율 입력: 파우치 = round(총 물량 × 파우치%/100)(파우치%는 0~100 으로 자름) · 박스 = 총 물량 − 파우치
//    호수별 박스 = round(박스 × 구성비/구성비 합) · 반올림 차이는 구성비가 0보다 큰 마지막 호수에 더하거나 뺀다.
//  - 호수별 수량 입력: 입력값 그대로 · 구성비% = 수량/박스 합(박스 합 0 이면 0).
//  - 마대 = ceil(파우치/마대당) · 1대 적재 = 적재함 부피 × 적재율 · 마대 1자리 = 1대 적재/1대당 마대
//  - 호수별 부피 = 가×세×높/1e6 × 수량 · 여유 계수 f = 1 + 여유율/100
//  - 마대분 = 마대 × f/일수/1대당 마대 · 박스분 = Σ부피 × f/일수/1대 적재(호수별 대수도 같은 식)
//  - 일 부피 = (마대 × 마대 1자리 + Σ부피) × f/일수 · 필요 대수 = 마대분 + 박스분(반올림 전 값으로 더한다)
//  - 배차 = ceil(필요 대수) · 마지막 차 적재율 = round((필요 대수 − (배차 − 1)) × 100)(딱 떨어지면 100 · 필요 0대면 0)
//  - 입력 보정(CLAMPS): 일수는 정수 반올림 후 최소 1 · 마대당·1대당 마대 최소 1 · 적재율 1~100 · 여유율 최소 0
//    · 적재함 최소 0.1 — 잘라서 계산하고 칸에 «N로 계산» 안내(notes)를 붙인다(오류가 아니다).
// 🔴 반올림은 Math.round(.5 올림)다 — 원본(Apps Script·JS)과 같다.
// 🔴 숫자 표기는 'en-US' 천 단위 쉼표 · 대수 소수 2자리 · 결과 부피 소수 1자리 · 호수표 부피 소수 2자리로 고정한다.

export const SIZES = [0, 1, 2, 3, 4];
export const DIM_KEYS = ['width_cm', 'length_cm', 'height_cm'];
export const MODE_LABELS = { ratio: '비율 입력', qty: '호수별 수량 입력' };
export const MAX_COUNT = 10000000;
export const MAX_DIM_CM = 1000;

// 계산기 칸 경로(화면 상태의 키). 비율·수량 칸은 모드마다 따로 둔다 — 전환할 때 서로 변환하지 않는다.
export const FIELD_PATHS = [
  'ratio.total', 'ratio.pouch_pct', ...SIZES.map((i) => `ratio.mix.${i}`),
  'qty.pouch', ...SIZES.map((i) => `qty.boxes.${i}`),
  'common.per_sack', 'common.days', 'common.safety_pct',
  'common.sacks_per_truck', 'common.truck_m3', 'common.load_pct',
  ...SIZES.flatMap((i) => DIM_KEYS.map((k) => `specs.${i}.${k}`)),
];

// 보정 칸 — 범위 밖의 유한수는 잘라서(round 면 정수 반올림 뒤) 계산하고 안내를 붙인다. 그 밖의 칸은 음수가 오류다.
export const CLAMPS = {
  'common.days': { min: 1, round: true },
  'common.per_sack': { min: 1 },
  'common.sacks_per_truck': { min: 1 },
  'common.load_pct': { min: 1, max: 100 },
  'common.safety_pct': { min: 0 },
  'common.truck_m3': { min: 0.1 },
};
const MIX_PATH = /^ratio\.mix\.\d$/;

const sum = (list) => list.reduce((a, b) => a + b, 0);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// ── 숫자 표기 ──────────────────────────────────────────────────────────────
const formatters = new Map();
const nf = (min, max) => {
  const key = `${min}:${max}`;
  if (!formatters.has(key)) {
    formatters.set(key, new Intl.NumberFormat('en-US', { minimumFractionDigits: min, maximumFractionDigits: max }));
  }
  return formatters.get(key);
};
export const fmtInt = (n) => nf(0, 0).format(n);
export const fmtNum = (n) => nf(0, 6).format(n);       // 끝의 0 없음(28.5)
export const fmtTrucks = (n) => nf(2, 2).format(n);    // 대수
export const fmtVol1 = (n) => nf(1, 1).format(n);      // 결과 부피
export const fmtVol2 = (n) => nf(2, 2).format(n);      // 호수표 부피
export const fmtPct1 = (n) => nf(1, 1).format(n);      // 공유 문구 비율
export const fmtRatio = (n) => nf(0, 1).format(n);     // 표의 비율·구성비(소수 1자리까지, 끝의 0 없음)

// ── 칸 검사 ────────────────────────────────────────────────────────────────
// 조사 — 표기 끝 글자가 0·3·6(영·삼·육)이면 '으로', 아니면 '로'.
const ro = (text) => (/[036]$/.test(text) ? '으로' : '로');

/** 보정 안내 — '1로 계산'·'100으로 계산'·'0.1로 계산'. */
export function clampNote(value) {
  const text = fmtNum(value);
  return `${text}${ro(text)} 계산`;
}

/**
 * 칸 하나 — { value, error, note }. 빈칸·숫자 아님(보정 칸이 아니면 음수도)은 value null · 오류.
 * 보정 칸(CLAMPS)은 범위로 잘라 value 로 쓰고, 원래 값과 다르면 note 를 붙인다. 쉼표는 무시한다.
 */
export function checkField(path, raw) {
  const text = String(raw ?? '').trim().replace(/,/g, '');
  if (text === '') return { value: null, error: '값을 입력하세요.', note: '' };
  const n = Number(text);
  if (!Number.isFinite(n)) return { value: null, error: '숫자를 입력하세요.', note: '' };
  const rule = CLAMPS[path];
  if (rule) {
    let v = rule.round ? Math.round(n) : n;
    v = Math.max(rule.min, v);
    if (rule.max !== undefined) v = Math.min(rule.max, v);
    return { value: v, error: '', note: v === n ? '' : clampNote(v) };
  }
  if (n < 0) return { value: null, error: '0 이상으로 입력하세요.', note: '' };
  return { value: n, error: '', note: '' };
}

/** 유효하면 숫자, 아니면 null(구성비 합계 0 은 applyInput 이 막는다 — 다른 칸을 알아야 한다). */
export const parseField = (path, raw) => checkField(path, raw).value;

// ── 상태 ──────────────────────────────────────────────────────────────────
const specsSorted = (specs) => [...(specs || [])].sort((a, b) => Number(a.size_no) - Number(b.size_no));

/** 서버 기본값 → 계산기 상태 { mode, raw(칸 글자), eff(마지막 유효값·보정값), errors, notes(보정 안내) }. */
export function stateFromDefaults(d) {
  const inputs = d.inputs;
  const specs = specsSorted(d.box_specs);
  const values = {
    'ratio.total': inputs.ratio.total,
    'ratio.pouch_pct': inputs.ratio.pouch_pct,
    'qty.pouch': inputs.qty.pouch,
    'common.per_sack': inputs.common.per_sack,
    'common.days': inputs.common.days,
    'common.safety_pct': inputs.common.safety_pct,
    'common.sacks_per_truck': inputs.common.sacks_per_truck,
    'common.truck_m3': inputs.common.truck_m3,
    'common.load_pct': inputs.common.load_pct,
  };
  SIZES.forEach((i) => {
    values[`ratio.mix.${i}`] = inputs.ratio.mix[i];
    values[`qty.boxes.${i}`] = inputs.qty.boxes[i];
    DIM_KEYS.forEach((k) => { values[`specs.${i}.${k}`] = specs[i] ? specs[i][k] : 0; });
  });
  const raw = {};
  const eff = {};
  FIELD_PATHS.forEach((p) => {
    const n = Number(values[p]);
    eff[p] = Number.isFinite(n) ? n : 0;
    raw[p] = values[p] === null || values[p] === undefined ? '' : String(values[p]);
  });
  return { mode: inputs.default_mode === 'qty' ? 'qty' : 'ratio', raw, eff, errors: {}, notes: {} };
}

/**
 * 칸 하나를 바꾼다 — raw 는 늘 바꾸고 eff 는 유효할 때만 바꾼다(보정 칸은 보정값).
 * 오류는 state.errors, 보정 안내는 state.notes(칸 → 문구)에 담는다 — 한 칸에 둘이 같이 있지 않다.
 */
export function applyInput(state, path, raw) {
  const { value, error, note } = checkField(path, raw);
  let message = error;
  if (!message && MIX_PATH.test(path)) {
    const mix = SIZES.map((i) => (`ratio.mix.${i}` === path ? value : state.eff[`ratio.mix.${i}`]));
    if (!(sum(mix) > 0)) message = '구성비 합계가 0이면 계산할 수 없습니다.';
  }
  const errors = { ...state.errors };
  const notes = { ...(state.notes || {}) };
  if (message) errors[path] = message;
  else delete errors[path];
  if (!message && note) notes[path] = note;
  else delete notes[path];
  return {
    ...state,
    raw: { ...state.raw, [path]: raw },
    eff: message ? state.eff : { ...state.eff, [path]: value },
    errors,
    notes,
  };
}

export const setMode = (state, mode) => ({ ...state, mode: mode === 'qty' ? 'qty' : 'ratio' });

const mixOf = (eff) => SIZES.map((i) => eff[`ratio.mix.${i}`]);
const boxesOf = (eff) => SIZES.map((i) => eff[`qty.boxes.${i}`]);
const specsOf = (eff) => SIZES.map((i) => ({
  size_no: i + 1,
  width_cm: eff[`specs.${i}.width_cm`],
  length_cm: eff[`specs.${i}.length_cm`],
  height_cm: eff[`specs.${i}.height_cm`],
}));

// ── 계산 ──────────────────────────────────────────────────────────────────
/** 반올림 전 호수별 몫 = 박스 × 구성비/구성비 합(합이 0 이하면 0). */
export function boxShares(box, mix) {
  const total = sum(mix);
  if (!(total > 0)) return SIZES.map(() => 0);
  return mix.map((m) => (box * m) / total);
}

/** 호수별 박스 수 — Math.round 로 나누고 차이는 구성비가 0보다 큰 마지막 호수에 더하거나 뺀다. */
export function splitBoxes(box, mix) {
  const out = boxShares(box, mix).map((v) => Math.round(v));
  if (!(sum(mix) > 0)) return out;
  let last = out.length - 1;
  while (last > 0 && !(mix[last] > 0)) last -= 1;
  out[last] += box - sum(out);
  return out;
}

/** 모드별 건수 { mode, total, pouch, box, boxes, mixPct, mixSum, pouchPct, boxPct }. */
export function resolveCounts(mode, eff) {
  if (mode === 'qty') {
    const pouch = eff['qty.pouch'];
    const boxes = boxesOf(eff);
    const box = sum(boxes);
    const total = pouch + box;
    const pouchPct = total > 0 ? (pouch / total) * 100 : 0;
    return {
      mode: 'qty',
      total,
      pouch,
      box,
      boxes,
      mixPct: boxes.map((b) => (box > 0 ? (b / box) * 100 : 0)),
      mixSum: box > 0 ? 100 : 0,
      pouchPct,
      boxPct: total > 0 ? 100 - pouchPct : 0,
    };
  }
  const total = eff['ratio.total'];
  const pct = clamp(eff['ratio.pouch_pct'], 0, 100);
  const pouch = Math.round((total * pct) / 100);
  const box = total - pouch;
  const mix = mixOf(eff);
  return {
    mode: 'ratio',
    total,
    pouch,
    box,
    boxes: splitBoxes(box, mix),
    mixPct: mix,
    mixSum: sum(mix),
    pouchPct: pct,
    boxPct: 100 - pct,
  };
}

/** 결과 — 마대·부피·대수·배차·마지막 차 적재율과 호수별 행. */
export function compute(counts, eff) {
  const perSack = eff['common.per_sack'];
  const days = eff['common.days'];
  const sacksPerTruck = eff['common.sacks_per_truck'];
  const truckLoad = (eff['common.truck_m3'] * eff['common.load_pct']) / 100;
  const sackSlot = truckLoad / sacksPerTruck;
  const factor = 1 + eff['common.safety_pct'] / 100;
  const sacks = counts.pouch > 0 ? Math.ceil(counts.pouch / perSack) : 0;
  const rows = specsOf(eff).map((s, i) => {
    const qty = counts.boxes[i] || 0;
    const volume = ((s.width_cm * s.length_cm * s.height_cm) / 1e6) * qty;
    return {
      ...s,
      mixPct: counts.mixPct[i],
      qty,
      volume,
      trucks: (volume * factor) / days / truckLoad,
    };
  });
  const boxVolume = sum(rows.map((r) => r.volume));
  const sackTrucks = (sacks * factor) / days / sacksPerTruck;
  const boxTrucks = (boxVolume * factor) / days / truckLoad;
  const dailyM3 = ((sacks * sackSlot + boxVolume) * factor) / days;
  const need = sackTrucks + boxTrucks;
  const clean = Number(need.toFixed(9));   // 부동소수 잡음 방어(3.0000000001 → 3)
  const dispatch = Math.ceil(clean);
  // 마지막 차 = 필요 대수 − 앞 차들(배차 − 1) — 딱 떨어지면(필요 5.00대) 1.00 → 100% · 필요 0대면 차가 없어 0%.
  const lastPct = clean > 0 ? Math.round((clean - (dispatch - 1)) * 100) : 0;
  return {
    pouch: counts.pouch,
    box: counts.box,
    sacks,
    perSack,
    days,
    sacksPerTruck,
    truckLoad,
    sackSlot,
    factor,
    rows,
    boxVolume,
    sackTrucks,
    boxTrucks,
    dailyM3,
    need,
    dispatch,
    lastPct,
  };
}

/** 결과 부제 — '배차 3대(올림) · 마지막 차 적재율 11% · 2일 분할'(일수 2 이상일 때만 끝 조각). */
export function subtitle(result) {
  const base = `배차 ${fmtInt(result.dispatch)}대(올림) · 마지막 차 적재율 ${result.lastPct}%`;
  return result.days >= 2 ? `${base} · ${fmtNum(result.days)}일 분할` : base;
}

/**
 * 결과 칩 — 마대당 20 이면 30, 30 이면 20 기준으로 다시 센 마대·마대분 { alt, sacks, trucks }. 그 밖의 값이면 null.
 * 마대 N = ceil(파우치/alt) · 마대분 = N × 여유 계수/일수/1대당 마대(compute 와 같은 식).
 */
export function altSack(counts, result) {
  let alt = null;
  if (result.perSack === 20) alt = 30;
  else if (result.perSack === 30) alt = 20;
  if (alt === null) return null;
  const sacks = counts.pouch > 0 ? Math.ceil(counts.pouch / alt) : 0;
  return { alt, sacks, trucks: (sacks * result.factor) / result.days / result.sacksPerTruck };
}

/** '30개/마대 기준이면 마대 567개 → 마대분 1.86대'(칩이 없으면 빈 글자). */
export function altSackText(a) {
  return a ? `${a.alt}개/마대 기준이면 마대 ${fmtInt(a.sacks)}개 → 마대분 ${fmtTrucks(a.trucks)}대` : '';
}

export const SCENARIOS = [
  { key: 'current', name: '현재', season: 'current', mix: 'current' },
  { key: 'winter', name: '동절기', season: 'winter', mix: 'current' },
  { key: 'winter_hanger', name: '동절기 + 행거', season: 'winter', mix: 'hanger' },
];

/**
 * 시나리오 비교 — 현재 / 동절기 / 동절기 + 행거 / 현재 입력값.
 * 총 물량은 비율 모드면 total, 수량 모드면 파우치 + 박스 합. 일수·여유율·적재 기준·규격은 현재 입력값을 쓴다.
 */
export function scenarios(eff, mode, presets) {
  const base = resolveCounts(mode, eff);
  const rows = SCENARIOS.map((sc) => {
    const season = presets.season[sc.season];
    const mix = presets.mix[sc.mix];
    const pct = clamp(season.pouch_pct, 0, 100);
    const pouch = Math.round((base.total * pct) / 100);
    const box = base.total - pouch;
    const counts = {
      mode: 'ratio', total: base.total, pouch, box, boxes: splitBoxes(box, mix),
      mixPct: [...mix], mixSum: sum(mix), pouchPct: pct, boxPct: 100 - pct,
    };
    const r = compute(counts, { ...eff, 'common.per_sack': season.per_sack });
    return {
      key: sc.key, name: sc.name, pouchPct: pct, boxPct: 100 - pct, perSack: season.per_sack,
      sacks: r.sacks, mix: counts.mixPct, dailyM3: r.dailyM3, need: r.need, dispatch: r.dispatch, lastPct: r.lastPct,
    };
  });
  const cur = compute(base, eff);
  rows.push({
    key: 'input', name: '현재 입력값', pouchPct: base.pouchPct, boxPct: base.boxPct, perSack: eff['common.per_sack'],
    sacks: cur.sacks, mix: base.mixPct, dailyM3: cur.dailyM3, need: cur.need, dispatch: cur.dispatch, lastPct: cur.lastPct,
  });
  return rows;
}

/**
 * 메신저 공유 문구 — 시나리오 줄은 3개(현재 입력값 행은 넣지 않는다) · 일수 2 이상이면 첫 줄 끝에 ' / N일 분할'
 * · 마지막 줄(빈 줄 없이)은 '※ 기준: 11톤 1대 마대 N개 · 적재율 N% / …'.
 */
export function shareText(state, presets) {
  const counts = resolveCounts(state.mode, state.eff);
  const r = compute(counts, state.eff);
  const split = r.days >= 2 ? ` / ${fmtNum(r.days)}일 분할` : '';
  const lines = [
    `[배차 계산] 총 ${fmtInt(counts.total)}건 · 파우치 ${fmtPct1(counts.pouchPct)}% : 박스 ${fmtPct1(counts.boxPct)}%${split}`,
    `파우치 ${fmtInt(counts.pouch)}건 → 마대 ${fmtInt(r.sacks)}개(마대당 ${fmtNum(r.perSack)}개)`,
    `박스 ${fmtInt(counts.box)}건 → ${fmtVol2(r.boxVolume)}㎥`,
    `일 필요 부피 ${fmtVol1(r.dailyM3)}㎥ · 11톤 ${fmtTrucks(r.need)}대(마대 ${fmtTrucks(r.sackTrucks)} + 박스 ${fmtTrucks(r.boxTrucks)})`,
    `배차 ${fmtInt(r.dispatch)}대(올림) · 마지막 차 적재율 ${r.lastPct}%`,
    '',
    '[시나리오]',
  ];
  scenarios(state.eff, state.mode, presets).slice(0, SCENARIOS.length).forEach((s) => {
    lines.push(`${s.name}(${fmtRatio(s.pouchPct)}:${fmtRatio(s.boxPct)} · 마대당 ${fmtNum(s.perSack)}) ${fmtTrucks(s.need)}대 → ${fmtInt(s.dispatch)}대`);
  });
  lines.push(
    `※ 기준: 11톤 1대 마대 ${fmtInt(state.eff['common.sacks_per_truck'])}개 · 적재율 ${fmtNum(state.eff['common.load_pct'])}%`
    + ' / 박스는 마대와 별도 적재, 호수별 규격 부피 기준',
  );
  return lines.join('\n');
}

// ── 프리셋 ────────────────────────────────────────────────────────────────
/** 지금 값이 어느 계절 프리셋과 같은가('current'·'winter'·null) — 파우치 비율·마대당을 본다. */
export function matchSeason(eff, presets) {
  const hit = ['current', 'winter'].find((k) => {
    const p = presets.season[k];
    return p && eff['ratio.pouch_pct'] === p.pouch_pct && eff['common.per_sack'] === p.per_sack;
  });
  return hit || null;
}

/**
 * 지금 값이 어느 박스 구성 프리셋과 같은가('current'·'hanger'·null).
 * 비율 모드는 구성비를, 수량 모드는 박스 수량이 «박스 합을 그 프리셋으로 나눈 값»과 같은지 본다(박스 합 0 이면 null).
 */
export function matchMix(eff, presets, mode = 'ratio') {
  const keys = ['current', 'hanger'];
  if (mode === 'qty') {
    const boxes = boxesOf(eff);
    const total = sum(boxes);
    if (!(total > 0)) return null;
    const hit = keys.find((k) => {
      const p = presets.mix[k];
      if (!Array.isArray(p) || p.length !== boxes.length) return false;
      const want = splitBoxes(total, p);
      return want.every((v, i) => v === boxes[i]);
    });
    return hit || null;
  }
  const mix = mixOf(eff);
  const hit = keys.find((k) => {
    const p = presets.mix[k];
    return Array.isArray(p) && p.length === mix.length && p.every((v, i) => v === mix[i]);
  });
  return hit || null;
}

const setValues = (state, values) => {
  const raw = { ...state.raw };
  const eff = { ...state.eff };
  const errors = { ...state.errors };
  const notes = { ...(state.notes || {}) };
  Object.entries(values).forEach(([path, value]) => {
    raw[path] = String(value);
    eff[path] = Number(value);
    delete errors[path];
    delete notes[path];
  });
  return { ...state, raw, eff, errors, notes };
};

/** 계절 프리셋 — 비율 모드의 파우치 비율과 마대당만 바꾼다(수량 모드 계산에는 마대당만 반영된다). */
export function applySeason(state, presets, key) {
  const p = presets.season[key];
  return setValues(state, { 'ratio.pouch_pct': p.pouch_pct, 'common.per_sack': p.per_sack });
}

/**
 * 박스 구성 프리셋 — 비율 모드면 구성비를 바꾸고, 수량 모드면 지금 박스 합을 그 구성비로 다시 나눠 호수별 수량을 채운다.
 * 다른 모드의 값은 건드리지 않는다(모드별 값 보존 — 수량 모드에서 눌러도 ratio.mix 는 그대로).
 */
export function applyMix(state, presets, key) {
  const mix = presets.mix[key];
  const values = {};
  if (state.mode === 'qty') {
    const boxes = splitBoxes(sum(boxesOf(state.eff)), mix);
    SIZES.forEach((i) => { values[`qty.boxes.${i}`] = boxes[i]; });
  } else {
    SIZES.forEach((i) => { values[`ratio.mix.${i}`] = mix[i]; });
  }
  return setValues(state, values);
}

// ── 브라우저 저장값 ─────────────────────────────────────────────────────────
const sortKeys = (v) => {
  if (Array.isArray(v)) return v.map(sortKeys);
  if (v && typeof v === 'object') {
    return Object.keys(v).sort().reduce((out, k) => ({ ...out, [k]: sortKeys(v[k]) }), {});
  }
  return v;
};

/**
 * 공용 기본값 지문 — inputs + (호수순) box_specs 를 키 정렬한 JSON. 프리셋은 넣지 않는다
 * (프리셋만 바뀌면 «공용 기본값을 바꿨습니다» 배너가 뜨지 않는다).
 */
export function defaultsFingerprint(d) {
  if (!d) return '';
  return JSON.stringify(sortKeys({ inputs: d.inputs, box_specs: specsSorted(d.box_specs) }));
}

/** 브라우저 저장값 { mode, raw } → 계산기 상태. 공용 기본값에서 시작해 모드를 적용하고, 저장된 글자 칸만 차례로 넣는다. */
export function stateFromSaved(saved, defaults) {
  let state = stateFromDefaults(defaults);
  if (saved && (saved.mode === 'ratio' || saved.mode === 'qty')) state = setMode(state, saved.mode);
  const raw = saved && saved.raw && typeof saved.raw === 'object' ? saved.raw : {};
  FIELD_PATHS.forEach((p) => {
    if (typeof raw[p] === 'string') state = applyInput(state, p, raw[p]);
  });
  return state;
}

const clone = (v) => JSON.parse(JSON.stringify(v));

/** 지금 화면 값 → 기본값 저장 본문(현재 모드·두 모드 값·공통·규격 + 프리셋 그대로). base_revision 은 부르는 쪽이 붙인다. */
export function payloadFromState(state, presets) {
  const e = state.eff;
  return {
    inputs: {
      default_mode: state.mode,
      ratio: { total: e['ratio.total'], pouch_pct: e['ratio.pouch_pct'], mix: mixOf(e) },
      qty: { pouch: e['qty.pouch'], boxes: boxesOf(e) },
      common: {
        per_sack: e['common.per_sack'],
        days: e['common.days'],
        safety_pct: e['common.safety_pct'],
        sacks_per_truck: e['common.sacks_per_truck'],
        truck_m3: e['common.truck_m3'],
        load_pct: e['common.load_pct'],
      },
    },
    presets: clone(presets),
    box_specs: specsOf(e),
  };
}

// ── 기본값 편집(서버 경로 그대로) ─────────────────────────────────────────────
export const SEASON_KEYS = [['current', '현재'], ['winter', '동절기']];
export const MIX_PRESET_KEYS = [['current', '현재 구성(%)'], ['hanger', '행거 시즌 구성(%)']];

// 편집 칸의 서버 경로 — 오류(422 field)와 같은 이름이다. 배열은 0부터 센 자리.
export const DEFAULT_FORM_PATHS = [
  'inputs.ratio.total', 'inputs.ratio.pouch_pct', ...SIZES.map((i) => `inputs.ratio.mix.${i}`),
  'inputs.qty.pouch', ...SIZES.map((i) => `inputs.qty.boxes.${i}`),
  'inputs.common.per_sack', 'inputs.common.days', 'inputs.common.safety_pct',
  'inputs.common.sacks_per_truck', 'inputs.common.truck_m3', 'inputs.common.load_pct',
  ...SIZES.flatMap((i) => DIM_KEYS.map((k) => `box_specs.${i}.${k}`)),
  ...SEASON_KEYS.flatMap(([k]) => [`presets.season.${k}.pouch_pct`, `presets.season.${k}.per_sack`]),
  ...MIX_PRESET_KEYS.flatMap(([k]) => SIZES.map((i) => `presets.mix.${k}.${i}`)),
];

const segments = (path) => path.split('.').map((p) => (/^\d+$/.test(p) ? Number(p) : p));

export function getPath(obj, path) {
  return segments(path).reduce((cur, key) => (cur === null || cur === undefined ? undefined : cur[key]), obj);
}

function setPath(obj, path, value) {
  const keys = segments(path);
  let cur = obj;
  keys.slice(0, -1).forEach((key, i) => {
    if (cur[key] === undefined) cur[key] = typeof keys[i + 1] === 'number' ? [] : {};
    cur = cur[key];
  });
  cur[keys[keys.length - 1]] = value;
}

/** 서버 기본값 → 편집 칸 { default_mode, values(경로 → 글자) }. */
export function formFromDefaults(d) {
  const data = { ...d, box_specs: specsSorted(d.box_specs) };
  const values = {};
  DEFAULT_FORM_PATHS.forEach((p) => {
    const v = getPath(data, p);
    values[p] = v === null || v === undefined ? '' : String(v);
  });
  return { default_mode: d.inputs.default_mode === 'qty' ? 'qty' : 'ratio', values };
}

/** 칸 글자 → 숫자. 빈칸은 null, 숫자가 아니면 NaN(검사에서 막힌다). 쉼표는 무시한다. */
export function toNumber(raw) {
  const text = String(raw ?? '').trim().replace(/,/g, '');
  if (text === '') return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : NaN;
}

/** 편집 칸 → 저장 본문(base_revision 은 부르는 쪽이 붙인다). */
export function payloadFromForm(form) {
  const body = {
    inputs: { default_mode: form.default_mode, ratio: { mix: [] }, qty: { boxes: [] }, common: {} },
    presets: { season: { current: {}, winter: {} }, mix: { current: [], hanger: [] } },
    box_specs: SIZES.map((i) => ({ size_no: i + 1 })),
  };
  DEFAULT_FORM_PATHS.forEach((p) => setPath(body, p, toNumber(form.values[p])));
  return body;
}

// 저장 검사 — 서버(services/dispatch_calc_defaults.validate)와 같은 규칙·같은 칸 경로. 실패하면 { field, message }.
const INVALID = 'DispatchDefaultsInvalid';
class Invalid extends Error {
  constructor(field, message) {
    super(message);
    this.name = INVALID;
    this.field = field;
  }
}

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

function vNumber(v, field, label) {
  if (!isNum(v)) throw new Invalid(field, `${label}에 숫자를 입력하세요.`);
  return v;
}

function vCount(v, field, label) {
  vNumber(v, field, label);
  if (!Number.isInteger(v)) throw new Invalid(field, `${label}은(는) 정수여야 합니다.`);
  if (v < 1) throw new Invalid(field, `${label}은(는) 0보다 커야 합니다.`);
  if (v > MAX_COUNT) throw new Invalid(field, `${label}은(는) ${fmtInt(MAX_COUNT)} 이하여야 합니다.`);
}

function vPositive(v, field, label, high = MAX_COUNT) {
  vNumber(v, field, label);
  if (v <= 0) throw new Invalid(field, `${label}은(는) 0보다 커야 합니다.`);
  if (v > high) throw new Invalid(field, `${label}은(는) ${fmtInt(high)} 이하여야 합니다.`);
}

function vPercent(v, field, label) {
  vNumber(v, field, label);
  if (v < 0 || v > 100) throw new Invalid(field, `${label}은(는) 0~100 사이여야 합니다.`);
}

function vFive(list, field, label, check) {
  if (!Array.isArray(list) || list.length !== SIZES.length) throw new Invalid(field, `${label}은(는) 1~5호 다섯 칸이어야 합니다.`);
  list.forEach((v, i) => check(v, `${field}.${i}`, `${label} ${i + 1}호`));
}

function vDimension(v, field, label) {
  vPositive(v, field, label, MAX_DIM_CM);
  if (Math.abs(Math.round(v * 100) / 100 - v) > 1e-9) throw new Invalid(field, `${label}은(는) 소수 둘째 자리까지 입력하세요.`);
}

const DIM_LABELS = { width_cm: '가로(cm)', length_cm: '세로(cm)', height_cm: '높이(cm)' };

/** 저장 본문 검사 — 통과하면 null. */
export function validateDefaults(body) {
  try {
    const { inputs, presets } = body;
    if (!['ratio', 'qty'].includes(inputs.default_mode)) {
      throw new Invalid('inputs.default_mode', '기본 입력 모드는 비율 입력·호수별 수량 입력 중 하나여야 합니다.');
    }
    vCount(inputs.ratio.total, 'inputs.ratio.total', '총 출고 물량(건)');
    vPercent(inputs.ratio.pouch_pct, 'inputs.ratio.pouch_pct', '파우치 비율(%)');
    vFive(inputs.ratio.mix, 'inputs.ratio.mix', '구성비(%)', vPositive);
    vCount(inputs.qty.pouch, 'inputs.qty.pouch', '파우치 수량(건)');
    vFive(inputs.qty.boxes, 'inputs.qty.boxes', '박스 수량(건)', vCount);
    const c = inputs.common;
    vCount(c.per_sack, 'inputs.common.per_sack', '마대당 파우치(개)');
    vCount(c.days, 'inputs.common.days', '출고 일수(일)');
    vPositive(c.safety_pct, 'inputs.common.safety_pct', '안전 여유율(%)', 100);
    vCount(c.sacks_per_truck, 'inputs.common.sacks_per_truck', '11톤 1대당 마대(개)');
    vPositive(c.truck_m3, 'inputs.common.truck_m3', '적재함 부피(㎥)');
    vPositive(c.load_pct, 'inputs.common.load_pct', '차량 적재율(%)', 100);
    SEASON_KEYS.forEach(([k, name]) => {
      const p = presets.season[k];
      vPercent(p.pouch_pct, `presets.season.${k}.pouch_pct`, `계절 프리셋 · ${name} · 파우치 비율(%)`);
      vCount(p.per_sack, `presets.season.${k}.per_sack`, `계절 프리셋 · ${name} · 마대당 파우치(개)`);
    });
    MIX_PRESET_KEYS.forEach(([k, name]) => {
      const field = `presets.mix.${k}`;
      const label = `박스 구성 프리셋 · ${name}`;
      vFive(presets.mix[k], field, label, vPositive);
      if (Math.round(sum(presets.mix[k]) * 1e6) / 1e6 !== 100) throw new Invalid(field, `${label} 합계가 100이어야 합니다.`);
    });
    const specs = body.box_specs;
    if (!Array.isArray(specs) || specs.length !== SIZES.length) throw new Invalid('box_specs', '박스 규격은 1~5호 다섯 개여야 합니다.');
    specs.forEach((s, i) => {
      DIM_KEYS.forEach((k) => vDimension(s[k], `box_specs.${i}.${k}`, `${s.size_no}호 ${DIM_LABELS[k]}`));
    });
    return null;
  } catch (e) {
    if (e && e.name === INVALID) return { field: e.field, message: e.message };
    throw e;
  }
}
